export interface DetentStop {
  /**
   * Height of the sheet at this stop, as a CSS length. Percentages are not
   * allowed — Silk measures detents against a track it sizes itself, so a `%`
   * resolves against the wrong box. Use `calc()` against the viewport var.
   */
  length: string;
  /** Announced on the grabber at this stop, e.g. "Half open". Must be translated. */
  label: string;
}

/**
 * Detent arithmetic for a persistent sheet.
 *
 * Silk numbers detents from 1, and the fully-expanded stop is implicit — it
 * has no entry in the list, so it is always `stops.length + 1`. That off-by-one
 * is the whole reason this is a named, tested module rather than inline
 * indexing.
 */

/** The 1-based index of the implicit fully-expanded stop. */
export function expandedDetent(stops: readonly DetentStop[]): number {
  return stops.length + 1;
}

/** Whether the sheet is at its fully-expanded stop, where the body scrolls. */
export function isExpanded(detent: number, stops: readonly DetentStop[]): boolean {
  return detent === expandedDetent(stops);
}

/** The label for a 1-based detent, or undefined if it is out of range. */
export function detentLabel(stops: readonly DetentStop[], expandedLabel: string, detent: number): string | undefined {
  if (detent === expandedDetent(stops)) return expandedLabel;
  return stops[detent - 1]?.label;
}

/**
 * A `%` length silently rides from the wrong edge, placing the sheet somewhere
 * plausible but wrong — so it is worth failing loudly in development. A
 * template-literal type cannot express this: `` `calc(${string})` `` still
 * admits `calc(50%)`, and call-site constants widen to `string` anyway.
 */
export function assertDetentLengths(stops: readonly DetentStop[]): void {
  for (const stop of stops) {
    if (stop.length.includes("%")) {
      throw new Error(
        `PersistentSheet detent "${stop.length}" uses a percentage. Silk measures detents against a track it sizes itself; express the stop as a calc() against --height-viewport instead.`,
      );
    }
  }
}
